import { useMemo } from 'react';
import { defineMessages } from 'react-intl';
import { useSecuritiesContext } from '../../../../contexts';
import { useIntl } from '../../../../hooks';
import type { Security } from '../../../../types';
import { IconName } from '../../../Icons';
import type { FilterableProperty } from '../types';
import {
  BASE_PAIR_FILTER_TOOLTIP,
  getBasePairFilterOptions,
  securityMatchesBasePairs,
} from './symbolCurrencyBasePairFilters';

const messages = defineMessages({
  basePair: {
    defaultMessage: 'Base Pair',
    id: 'Filters.FilterBuilder.filters.basePair',
  },
});

/**
 * Creates a filter property definition for selecting Spot base/quote pairs.
 *
 * @param availableSecurities - Optional list used only to derive dropdown options. If omitted, all securities from the Securities Context will be used.
 */
export function useBasePairFilter(
  availableSecurities?: Pick<Security, 'Symbol' | 'BaseCurrency' | 'QuoteCurrency' | 'ProductType'>[]
) {
  const { formatMessage } = useIntl();
  const { securitiesList, securitiesBySymbol } = useSecuritiesContext();
  const options = useMemo(
    () => getBasePairFilterOptions(availableSecurities ?? securitiesList),
    [availableSecurities, securitiesList]
  );

  return useMemo(
    () =>
      ({
        key: 'BasePairs',
        field: 'Symbol',
        label: formatMessage(messages.basePair),
        icon: IconName.ArrowLeftRight,
        tooltip: BASE_PAIR_FILTER_TOOLTIP,
        options,
        getOptionLabel: (option: string) => option,
        clearUnsupportedSelectionsOnInit: false,
        fieldFilter: (symbol: string, selections: string[]) =>
          securityMatchesBasePairs(securitiesBySymbol.get(symbol), selections),
      }) as const satisfies FilterableProperty,
    [formatMessage, options, securitiesBySymbol]
  );
}
